import { CSSProperties, ReactNode, Suspense, lazy } from 'react';

import classNames from 'classnames';
import { ToastContainer } from 'react-toastify';

import { DrawerController } from '@src/HOC';
import { MainScreenContainer } from '@src/containers';
import { useRoomContext } from '@src/contexts';
import { isWebView } from '@src/helpers';
import { OLoader } from '@src/shared';
import { useDrawerStore, useMediaSettingsStore, useRoomActionsStore, useRoomStore } from '@src/stores';
import { useMediaEffects } from '@src/hooks';

import MobilePipContent from '../../components/MobilePipComponent';
import ExitRoomModal from '../../components/ExitRoomModal';
import { useShallow } from 'zustand/react/shallow';
import { EMeetingType } from '@src/types';

const AnimatedReactions = lazy(() => import('../../components/AnimatedReactions'));
const RoomHeader = lazy(() => import('@src/containers/RoomHeader'));
const RoomFooter = lazy(() => import('@src/containers/RoomFooter'));
const InviteToMagicPenModal = lazy(() => import('@src/components/InviteToMagicPenModal'));

type RoomLayoutProps = {
  main?: ReactNode;
  mediaPreview?: ReactNode;
  extras?: ReactNode;
};

const RoomLayout = ({ main, mediaPreview, extras }: RoomLayoutProps) => {
  // --- Contexts ---
  const { isMobileDevice, isLoading, isCurrentPeerSharing, isHost, shareScreenStream, isFullyJoined } =
    useRoomContext();

  // --- Global Effects ---
  useMediaEffects();

  // --- Stores ---
  const isDrawerOpen = useDrawerStore(useShallow((state) => state.isDrawerOpen));

  const { meetingType, isJoinedRoom } = useRoomStore(
    useShallow((state) => ({
      meetingType: state.meetingType,
      isJoinedRoom: state.isJoinedRoom,
    }))
  );
  const isMeetingView = meetingType === EMeetingType.MEETING;

  const isOpenMediaPreview = useMediaSettingsStore(
    useShallow((state) => (isMeetingView ? state.mediaSettings.isOpenMediaPreview : false))
  );
  const isPipActive = useRoomActionsStore(useShallow((state) => state.pipSettings.isActive));

  // --- Logic ---

  // 1) Only “joined room” without a global loading state should ever show the main view
  const shouldRenderRoomView = isFullyJoined && (isMeetingView ? !isOpenMediaPreview : true);
  // 2) Meeting-only pre-join drawer on mobile
  const shouldRenderPreJoinDrawer = !isJoinedRoom && isMobileDevice;
  // 3) Drawer mounted only when it’s actually visible in UI
  const showDrawer = shouldRenderRoomView || shouldRenderPreJoinDrawer;
  // Show Drawer only if joined or if user is in media preview and mobile device (For virtual background case)
  const isDrawerAffectingLayout = showDrawer ? isDrawerOpen : false;

  if (isPipActive && isMobileDevice) {
    return (
      <>
        <MobilePipContent />
        {extras}
      </>
    );
  }

  const containerClassName = classNames('meet-room', 'bg-main-color-500', {
    'disabled-box': isLoading,
  });

  const containerStyle: CSSProperties = {
    height: isMobileDevice ? '100%' : '100vh',
  };

  const toastClassName = classNames('toast-container', {
    'drawer-opened': isDrawerAffectingLayout && !isMobileDevice,
    'toaster-mobile': isMobileDevice,
  });

  return (
    <>
      <div className={containerClassName} style={containerStyle}>
        <MainScreenContainer isDrawerOpen={isDrawerAffectingLayout && !isMobileDevice}>
          <Suspense fallback={<OLoader />}>
            {shouldRenderRoomView && !isWebView() && isFullyJoined && <RoomHeader />}

            {shouldRenderRoomView ? main : isMeetingView && isOpenMediaPreview ? mediaPreview : null}

            {shouldRenderRoomView && (
              <Suspense fallback={null}>
                <AnimatedReactions />
              </Suspense>
            )}
          </Suspense>

          {!isFullyJoined && !isOpenMediaPreview && <OLoader />}

          {<ExitRoomModal roomType={isMeetingView ? 'meet' : 'webinar'} />}
        </MainScreenContainer>

        {showDrawer && <DrawerController />}

        {shouldRenderRoomView && (
          <>
            <Suspense fallback={<OLoader />}>
              {isWebView() ? !isDrawerOpen ? <RoomFooter /> : null : isFullyJoined && <RoomFooter />}
            </Suspense>
            <ToastContainer className={toastClassName} />
          </>
        )}
      </div>
      {extras}
      {isJoinedRoom && (isCurrentPeerSharing || (isHost && !!shareScreenStream)) && (
        <Suspense fallback={null}>
          <InviteToMagicPenModal />
        </Suspense>
      )}
    </>
  );
};

export default RoomLayout;
